import VueI18n, { DateTimeFormats } from 'vue-i18n';
import Vue from 'vue';

import { getLanguageFromBrowser } from '@/helpers/locale-helper';

import { transformTranslations } from '@/stores/language.store';
import { getAxiosCached } from './helpers/axios-helper';

export const DEFAULT_LANGUAGE_FALLBACK = 'en';
export const DEFAULT_LOCALE_FALLBACK = 'en-US';

Vue.use(VueI18n);

// @todo replace with a more dynamic approach
const dateTimeFormats: DateTimeFormats = {
	'en-US': {
		short: {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
		},
		long: {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
			weekday: 'long',
			hour: 'numeric',
			minute: 'numeric',
		},
	},
	'de-DE': {
		short: {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
		},
		long: {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
			weekday: 'long',
			hour: 'numeric',
			minute: 'numeric',
		},
	},
} as DateTimeFormats;

/**
 * set translatables from local json as fallback when translatables cannot be downloaded
 *
 * @param {string} lang
 *
 * @returns {boolean}
 */
export const verifyTranslations = async (i18n: VueI18n): Promise<boolean> => {
	if (i18n && Object.keys(i18n.messages).length === 0) {
		const lang = getLanguageFromBrowser() || i18n.locale || DEFAULT_LANGUAGE_FALLBACK;
		try {
			return await setTranslatables(i18n, lang);
		} catch (error) {
			return setTranslatables(i18n, DEFAULT_LANGUAGE_FALLBACK);
		}
	}
	return false;
};

/**
 * get local json for `lang` translatable
 * tranform json and set it to i18n
 *
 * @param lang
 *
 * @returns {Promise<boolean>}
 */
const setTranslatables = async (i18n: VueI18n, lang: string): Promise<boolean> => {
	const jsonUrl = `/${global.ASSETS_DIR}/translatables/${lang}.json`;
	try {
		const json = await getAxiosCached().get<TranslationResponse>(jsonUrl);
		const translatables = json.data;

		if (!translatables) {
			throw new Error(`Broken local translatable for ${lang}`);
		}
		const transformed = transformTranslations(translatables);
		i18n.locale = lang;
		await i18n.setLocaleMessage(lang, transformed);
		verifyTranslations(i18n);
	} catch (error) {
		throw new Error(`${lang} ${error}`);
	}
	return true;
};

export const createI18n = () =>
	new VueI18n({
		locale: DEFAULT_LOCALE_FALLBACK, // @todo set locale
		fallbackLocale: DEFAULT_LOCALE_FALLBACK,
		messages: {},
		dateTimeFormats,
	});
